// src/pages/ingresos/components/PendingIntakeInstrumentsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import IntakeInstrumentModal from "./IntakeInstrumentModal";

export default function PendingIntakeInstrumentsModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]); // filas crudas desde la vista
  const [search, setSearch] = useState("");
  const [expandedClientId, setExpandedClientId] = useState(null);

  // para abrir el modal de edición de un intake
  const [editingIntake, setEditingIntake] = useState(null);
  // { intakeId, clientId, clientName }

  useEffect(() => {
    if (!open) return;

    let active = true;

    async function load() {
      try {
        setLoading(true);
        setErr("");

        const { data, error } = await supabase
          .from("v_pending_intake_instruments")
          .select(
            [
              "client_id",
              "client_name",
              "intake_id",
              "intake_code",
              "lab_id",
              "tag",
              "serial",
              "brand",
              "model",
              "intake_created_at",
            ].join(", ")
          )
          .order("client_name", { ascending: true })
          .order("intake_code", { ascending: true })
          .order("lab_id", { ascending: true });

        if (!active) return;
        if (error) throw error;

        setRows(data || []);
      } catch (e) {
        console.error("[PendingIntakeInstrumentsModal] load", e);
        if (active) setErr(e.message || "No se pudieron cargar los instrumentos.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [open]);

  // aplicar filtro de búsqueda
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      const haystack = [
        r.client_name,
        r.intake_code,
        r.tag,
        r.serial,
        r.brand,
        r.model,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search]);

  // agrupar por cliente
  const groups = useMemo(() => {
    const byClient = new Map();

    for (const r of filteredRows) {
      if (!byClient.has(r.client_id)) {
        byClient.set(r.client_id, {
          client_id: r.client_id,
          client_name: r.client_name,
          items: [],
        });
      }
      byClient.get(r.client_id).items.push(r);
    }

    return Array.from(byClient.values());
  }, [filteredRows]);

  const totalItems = filteredRows.length;

  function handleBackdropClick(e) {
    // cerrar solo si clic en el fondo oscuro
    if (e.target === e.currentTarget) onClose?.();
  }

  function handlePrintIntake(intakeId) {
    if (!intakeId) return;
    window.open(
      `/ingresos/comprobante?id=${encodeURIComponent(intakeId)}`,
      "_blank"
    );
  }

  function handleEditIntake(group, intakeId) {
    if (!intakeId || !group) return;
    setEditingIntake({
      intakeId,
      clientId: group.client_id,
      clientName: group.client_name,
    });
  }

  if (!open) return null;

  return (
    <>
      {/* MODAL PRINCIPAL DE LISTA */}
      <div
        className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4"
        onMouseDown={handleBackdropClick}
      >
        <div
          className="w-full max-w-5xl max-h-[80vh] rounded-2xl bg-[#0F1115] text-white border border-white/10 shadow-2xl flex flex-col"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">
                Instrumentos en intake (pendientes de asignar OS)
              </h2>
              <p className="text-xs text-slate-300 mt-1">
                Se muestran los instrumentos cargados en ingresos (intakes
                abiertos) que aún no fueron vinculados a una Orden de Servicio
                activa.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs px-2 py-1 rounded-full bg-sky-500/15 text-sky-200 border border-sky-500/40">
                Total instrumentos: <strong>{totalItems}</strong>
              </div>
              <button
                className="px-3 py-1.5 rounded-md border border-white/20 hover:bg-white/10 text-sm"
                onClick={onClose}
              >
                Cerrar
              </button>
            </div>
          </div>

          {/* Buscador */}
          <div className="px-5 pt-3 pb-2 border-b border-white/10">
            <input
              className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-sm"
              placeholder="Ej: ACME, 123456, FLUKE..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Contenido */}
          <div className="flex-1 overflow-auto px-5 py-3 space-y-3 text-xs">
            {loading && (
              <div className="text-slate-300">Cargando instrumentos…</div>
            )}

            {err && !loading && (
              <div className="text-red-400 text-sm">{err}</div>
            )}

            {!loading && !err && groups.length === 0 && (
              <div className="text-slate-300 text-sm">
                No se encontraron instrumentos pendientes de intake.
              </div>
            )}

            {!loading &&
              !err &&
              groups.map((group) => {
                const { client_id, client_name, items } = group;
                const intakesMap = new Map();
                items.forEach((it) => {
                  if (!intakesMap.has(it.intake_id)) {
                    intakesMap.set(it.intake_id, {
                      intake_id: it.intake_id,
                      intake_code: it.intake_code,
                    });
                  }
                });
                const intakes = Array.from(intakesMap.values());
                const onlyOneIntake = intakes.length === 1;
                const firstIntake = onlyOneIntake ? intakes[0] : null;

                const expanded = expandedClientId === client_id;

                return (
                  <div
                    key={client_id}
                    className="rounded-xl bg-black/40 border border-white/10 overflow-hidden"
                  >
                    {/* Cabecera del cliente */}
                    <button
                      type="button"
                      className="w-full px-4 py-2 flex items-center justify-between gap-3 hover:bg-white/5"
                      onClick={() =>
                        setExpandedClientId(
                          expanded ? null : client_id
                        )
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-7 w-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold">
                          {(client_name || "?").charAt(0)}
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium">
                            {client_name || "Cliente sin nombre"}
                          </div>
                          <div className="text-[11px] text-slate-400">
                            {items.length} instrumento
                            {items.length === 1 ? "" : "s"} en intake
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {onlyOneIntake && firstIntake && (
                          <>
                            <button
                              type="button"
                              className="px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-[11px]"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditIntake(group, firstIntake.intake_id);
                              }}
                            >
                              Editar {firstIntake.intake_code}
                            </button>
                            <button
                              type="button"
                              className="px-2.5 py-1 rounded-md bg-sky-600 hover:bg-sky-500 text-[11px]"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintIntake(firstIntake.intake_id);
                              }}
                            >
                              Imprimir {firstIntake.intake_code}
                            </button>
                          </>
                        )}
                        <span className="text-lg leading-none">
                          {expanded ? "▾" : "▸"}
                        </span>
                      </div>
                    </button>

                    {/* Tabla de instrumentos del cliente */}
                    {expanded && (
                      <div className="border-t border-white/10 bg-black/40">
                        <table className="min-w-full text-[11px]">
                          <thead>
                            <tr className="bg-black/60">
                              <th className="px-3 py-1 text-left">Intake</th>
                              <th className="px-3 py-1 text-left">SERV Nro.</th>
                              <th className="px-3 py-1 text-left">Tag</th>
                              <th className="px-3 py-1 text-left">
                                Descripción
                              </th>
                              <th className="px-3 py-1 text-left">Serie</th>
                              <th className="px-3 py-1 text-left">
                                Fecha ingreso
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((it, idx) => {
                              const desc = [it.brand, it.model]
                                .filter(Boolean)
                                .join(" ");
                              const date = it.intake_created_at
                                ? new Date(
                                    it.intake_created_at
                                  ).toLocaleDateString("es-AR")
                                : "-";

                              return (
                                <tr
                                  key={`${it.intake_id}-${it.lab_id}-${idx}`}
                                  className="border-t border-white/5"
                                >
                                  <td className="px-3 py-1">
                                    {it.intake_code || "—"}
                                  </td>
                                  <td className="px-3 py-1">
                                    {it.lab_id != null
                                      ? String(it.lab_id).padStart(7, "0")
                                      : "—"}
                                  </td>
                                  <td className="px-3 py-1">
                                    {it.tag || "—"}
                                  </td>
                                  <td className="px-3 py-1">
                                    {desc || "(sin descripción)"}
                                  </td>
                                  <td className="px-3 py-1">
                                    {it.serial || "—"}
                                  </td>
                                  <td className="px-3 py-1">{date}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Footer pequeño */}
          <div className="px-5 py-2 border-t border-white/10 text-[11px] text-slate-400 flex justify-between items-center">
            <span>
              Podés editar un intake desde el botón{" "}
              <strong>Editar IN-XX</strong> o imprimir la constancia desde{" "}
              <strong>Imprimir IN-XX</strong>.
            </span>
            <span>
              Total: <strong>{totalItems}</strong> instrumento
              {totalItems === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      {/* MODAL PARA EDITAR / AGREGAR / QUITAR INSTRUMENTOS DE UN INTAKE */}
      {editingIntake && (
        <IntakeInstrumentModal
          open={true}
          onClose={() => setEditingIntake(null)}
          intakeId={editingIntake.intakeId}
          client={{
            id: editingIntake.clientId,
            name: editingIntake.clientName,
          }}
          // modo "intake": alta + link + quitar desde el panel derecho
          mode="intake"
        />
      )}
    </>
  );
}
